import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import type { ColumnOrderState, OnChangeFn, SortingState } from "@tanstack/react-table";

import {
  isReservedParamKey,
  useReportParams,
  type ReportParams,
  type UseReportParamsOptions,
} from "./use-report-params";

// URL-search-param state for a list screen: ?page=3&size=50&sort=visits:desc&q=…&cols=…
// on top of everything useReportParams already owns (?preset, ?tz, ?view and
// the free-form filter keys).
//
// Every list in this app had its own answer — some kept the filters in the URL
// and the page in useState, some kept everything in useState — so a link to
// "clicks, page 3, sorted by revenue" opened page 1 of the default sort, and
// the browser's back button moved between screens but never between pages.
// One hook, one vocabulary, and the whole state of a list is in its address.

const PAGE_KEY = "page";
const SIZE_KEY = "size";
const SORT_KEY = "sort";
const SEARCH_KEY = "q";
const COLUMNS_KEY = "cols";

export const DEFAULT_PAGE_SIZES = [25, 50, 100, 250] as const;

export interface TablePaginationState {
  pageIndex: number;
  pageSize: number;
}

/**
 * `visits:desc,name:asc` → SortingState. Unparseable entries are dropped
 * rather than thrown: a hand-edited URL should degrade to the default sort,
 * not to a blank screen. Pure, exported for testing.
 */
export function parseSort(raw: string | null | undefined): SortingState {
  if (!raw) return [];
  const out: SortingState = [];
  for (const part of raw.split(",")) {
    const [id, dir] = part.split(":");
    if (!id) continue;
    if (dir !== undefined && dir !== "asc" && dir !== "desc") continue;
    out.push({ id, desc: dir === "desc" });
  }
  return out;
}

/** SortingState → `visits:desc`. Empty state serialises to "" (no param). Pure. */
export function formatSort(sorting: SortingState): string {
  return sorting.map((s) => `${s.id}:${s.desc ? "desc" : "asc"}`).join(",");
}

/**
 * `name,clicks,profit` → a column order. Blank ids are dropped; the list is
 * deliberately NOT checked against the table's columns, because a PARTIAL
 * order is the normal case: someone arranged four columns, a fifth shipped
 * later, and TanStack keeps every id it was not told about in its declared
 * position behind the ones it was. Pure, exported for testing.
 */
export function parseColumnOrder(raw: string | null | undefined): ColumnOrderState {
  if (!raw) return [];
  return raw.split(",").filter(Boolean);
}

/** ColumnOrderState → `name,clicks,profit`. Empty serialises to "". Pure. */
export function formatColumnOrder(order: ColumnOrderState): string {
  return order.filter(Boolean).join(",");
}

/** 1-based in the URL, 0-based in TanStack. Junk and zero resolve to page 1. Pure. */
export function resolvePageIndex(raw: string | null | undefined): number {
  const n = Number.parseInt(raw ?? "", 10);
  return Number.isFinite(n) && n > 1 ? n - 1 : 0;
}

/** A size the screen actually offers, or its default. Pure. */
export function resolvePageSize(
  raw: string | null | undefined,
  fallback: number,
  options: readonly number[],
): number {
  const n = Number.parseInt(raw ?? "", 10);
  return options.includes(n) ? n : fallback;
}

export interface UseTableParamsOptions extends UseReportParamsOptions {
  /** Page size when the URL has none / one this screen does not offer. */
  defaultPageSize?: number;
  /** The sizes the footer offers, and the only ones accepted from the URL. */
  pageSizeOptions?: readonly number[];
  /** Sort applied when the URL carries none. Not written to the URL. */
  defaultSort?: SortingState;
  /** Column order applied when the URL carries none. Not written to the URL. */
  defaultColumnOrder?: ColumnOrderState;
}

export interface TableParams extends ReportParams {
  /** 0-based, for TanStack and for `offset = pageIndex * pageSize`. */
  pageIndex: number;
  pageSize: number;
  /** The two together, for a `DataTable`'s pagination prop. */
  pagination: TablePaginationState;
  pageSizeOptions: readonly number[];
  sorting: SortingState;
  /**
   * The ids of the columns, in the order the reader arranged them. A partial
   * list is valid — see `parseColumnOrder`.
   */
  columnOrder: ColumnOrderState;
  /** The free-text box. Its own key so `filters` stays dimensions-only. */
  search: string;
  setPageIndex: (pageIndex: number) => void;
  setPageSize: (pageSize: number) => void;
  setSorting: (sorting: SortingState) => void;
  /** The same, in the shape a table's `onSortingChange` wants. */
  onSortingChange: OnChangeFn<SortingState>;
  setColumnOrder: (order: ColumnOrderState) => void;
  /** The same, in the shape a table's `onColumnOrderChange` wants. */
  onColumnOrderChange: OnChangeFn<ColumnOrderState>;
  setSearch: (search: string) => void;
  /** Clears the search and every non-reserved key, and returns to page 1. */
  resetFilters: () => void;
}

export function useTableParams(options: UseTableParamsOptions = {}): TableParams {
  const {
    defaultPageSize = 50,
    pageSizeOptions = DEFAULT_PAGE_SIZES,
    defaultSort,
    defaultColumnOrder,
    ...reportOptions
  } = options;

  const report = useReportParams(reportOptions);
  const [searchParams, setSearchParams] = useSearchParams();

  const rawSort = searchParams.get(SORT_KEY);
  const rawColumnOrder = searchParams.get(COLUMNS_KEY);
  const pageIndex = resolvePageIndex(searchParams.get(PAGE_KEY));
  const pageSize = resolvePageSize(searchParams.get(SIZE_KEY), defaultPageSize, pageSizeOptions);
  const search = searchParams.get(SEARCH_KEY) ?? "";

  const sorting = useMemo(() => {
    const parsed = parseSort(rawSort);
    return parsed.length > 0 ? parsed : (defaultSort ?? []);
    // defaultSort is a literal at most call sites; comparing it by its
    // serialisation keeps a new array identity from resorting the table.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawSort, formatSort(defaultSort ?? [])]);

  const columnOrder = useMemo(() => {
    const parsed = parseColumnOrder(rawColumnOrder);
    return parsed.length > 0 ? parsed : (defaultColumnOrder ?? []);
    // Same reason as `sorting` above: compared by serialisation so a fresh
    // array literal at the call site does not re-order the table every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawColumnOrder, formatColumnOrder(defaultColumnOrder ?? [])]);

  const pagination = useMemo<TablePaginationState>(
    () => ({ pageIndex, pageSize }),
    [pageIndex, pageSize],
  );

  /**
   * One write, and the page comes back to 1 on anything that changes which
   * rows exist. Three screens used to do this in an effect keyed on a
   * hand-concatenated string of every filter; the effect fired after the
   * query had already gone out for a page that was no longer there.
   */
  const write = useCallback(
    (mutate: (next: URLSearchParams) => void, resetPage = true) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          if (resetPage) next.delete(PAGE_KEY);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setPageIndex = useCallback(
    (i: number) => {
      write((next) => {
        if (i > 0) next.set(PAGE_KEY, String(i + 1));
        else next.delete(PAGE_KEY);
      }, false);
    },
    [write],
  );

  const setPageSize = useCallback(
    (size: number) => {
      write((next) => {
        if (size !== defaultPageSize) next.set(SIZE_KEY, String(size));
        else next.delete(SIZE_KEY);
      });
    },
    [write, defaultPageSize],
  );

  // Sorting keeps the page: a re-sort of a server-paginated list changes which
  // rows are on page 3, and staying there is what the user asked for.
  const setSorting = useCallback(
    (next: SortingState) => {
      const serialised = formatSort(next);
      write((sp) => {
        if (serialised && serialised !== formatSort(defaultSort ?? [])) sp.set(SORT_KEY, serialised);
        else sp.delete(SORT_KEY);
      }, false);
    },
    [write, defaultSort],
  );

  const onSortingChange = useCallback<OnChangeFn<SortingState>>(
    (updater) => setSorting(typeof updater === "function" ? updater(sorting) : updater),
    [setSorting, sorting],
  );

  // Rearranging columns keeps the page, for the same reason sorting does: it
  // changes how the rows are read, not which rows exist.
  const setColumnOrder = useCallback(
    (next: ColumnOrderState) => {
      const serialised = formatColumnOrder(next);
      write((sp) => {
        if (serialised && serialised !== formatColumnOrder(defaultColumnOrder ?? [])) {
          sp.set(COLUMNS_KEY, serialised);
        } else {
          sp.delete(COLUMNS_KEY);
        }
      }, false);
    },
    [write, defaultColumnOrder],
  );

  const onColumnOrderChange = useCallback<OnChangeFn<ColumnOrderState>>(
    (updater) => setColumnOrder(typeof updater === "function" ? updater(columnOrder) : updater),
    [setColumnOrder, columnOrder],
  );

  const setSearch = useCallback(
    (q: string) => {
      write((next) => {
        if (q) next.set(SEARCH_KEY, q);
        else next.delete(SEARCH_KEY);
      });
    },
    [write],
  );

  // A filter decides which rows exist, so both setters go back to page 1 —
  // the report hook's own setFilters keeps the page, which on a list leaves
  // the reader on a page the narrowed list no longer has.
  const setFilters = useCallback(
    (patch: Record<string, string | null>) => {
      for (const key of Object.keys(patch)) {
        if (isReservedParamKey(key)) throw new Error(`useTableParams: "${key}" is a reserved param`);
      }
      write((next) => {
        for (const [key, value] of Object.entries(patch)) {
          if (value) next.set(key, value);
          else next.delete(key);
        }
      });
    },
    [write],
  );

  const setFilter = useCallback((key: string, value: string | null) => setFilters({ [key]: value }), [setFilters]);

  // The column arrangement is reserved, so "clear filters" leaves it alone —
  // it describes how the reader reads the list, not which rows are in it.
  const resetFilters = useCallback(() => {
    write((next) => {
      for (const key of [...next.keys()]) {
        if (!isReservedParamKey(key)) next.delete(key);
      }
      next.delete(SEARCH_KEY);
    });
  }, [write]);

  return {
    ...report,
    setFilter,
    setFilters,
    pageIndex,
    pageSize,
    pagination,
    pageSizeOptions,
    sorting,
    columnOrder,
    search,
    onSortingChange,
    setColumnOrder,
    onColumnOrderChange,
    setPageIndex,
    setPageSize,
    setSorting,
    setSearch,
    resetFilters,
  };
}
